import type { Document } from '@/hooks/useDocuments';
import type { Folder } from '@/hooks/useFolders';
import { getSubtreeFolderIdsByPathPrefix, normalizeFolderPath } from '@/lib/folderPathUtils';
import {
  collectFileReferenceIdsInFolders,
  isCompletedNonDraftDocument,
  isDocumentInFolders,
} from '@/utils/document-utils';

export const CLIENT_DOCUMENTS_SLUG = 'client-documents';
export const CLIENT_DOCUMENTS_NAME = 'Client Documents';
const CLIENT_DOCUMENTS_CANONICAL_PATH = `/${CLIENT_DOCUMENTS_SLUG}`;

export function isUnderClientDocumentsTree(normalizedPath: string): boolean {
  const p = normalizeFolderPath(normalizedPath);
  return p === CLIENT_DOCUMENTS_CANONICAL_PATH || p.startsWith(`${CLIENT_DOCUMENTS_CANONICAL_PATH}/`);
}

function isRootFolder(folder: Folder): boolean {
  return folder.depth === 0 && (folder.parent_folder_id === null || folder.parent_folder_id === undefined);
}

export function isClientDocumentsRoot(folder: Folder): boolean {
  if (!isRootFolder(folder)) {
    return false;
  }

  // Canonical detection: path/slug first.
  const normalizedPath = normalizeFolderPath(folder.path);
  if (normalizedPath === CLIENT_DOCUMENTS_CANONICAL_PATH) {
    return true;
  }

  // Fallback for older data where canonical path may have drifted.
  return folder.name.trim().toLowerCase() === CLIENT_DOCUMENTS_NAME.toLowerCase();
}

export function findClientDocumentsRoot(folders: Folder[]): Folder | undefined {
  return folders.find((f) => isClientDocumentsRoot(f));
}

export function getClientDocumentsSubtreeFolderIds(folders: Folder[]): Set<number> {
  const root = findClientDocumentsRoot(folders);
  if (!root) return new Set<number>();
  return getSubtreeFolderIdsByPathPrefix(root, folders);
}

export function getClientDocumentsSubtreeFolderGuids(folders: Folder[]): Set<string> {
  const subtreeIds = getClientDocumentsSubtreeFolderIds(folders);
  const guids = new Set<string>();
  for (const f of folders) {
    if (subtreeIds.has(f.folder_id)) {
      guids.add(f.guid);
    }
  }
  return guids;
}

/**
 * Distinct document guids in a set of folder subtrees.
 *
 * No eligibility filter — needs to cover every visible picker row and on-save
 * dedup of `file:X` vs. covering `folder:Y`. Budget filtering is applied in
 * `collectExplicitSelectionDocumentGuids`.
 */
export function collectDocumentGuidsInFolderSubtrees(
  folderGuids: Iterable<string>,
  folders: Folder[],
  documents: Document[],
): Set<string> {
  const folderByGuid = new Map(folders.map((f) => [f.guid, f]));
  const folderGuidById = new Map(folders.map((f) => [f.folder_id, f.guid]));
  const guids = new Set<string>();

  for (const folderGuid of folderGuids) {
    const root = folderByGuid.get(folderGuid);
    if (!root) continue;
    const subtreeIds = getSubtreeFolderIdsByPathPrefix(root, folders);
    const subtreeGuids = new Set<string>();
    for (const fid of subtreeIds) {
      const g = folderGuidById.get(fid);
      if (g) subtreeGuids.add(g);
    }
    for (const d of documents) {
      if (isDocumentInFolders(d, subtreeIds, subtreeGuids)) {
        guids.add(d.guid);
      }
    }
  }
  return guids;
}

/**
 * Distinct document guids covered by explicit file + folder picks (folder = full subtree from `documents`).
 *
 * Applies `isCompletedNonDraftDocument` so the count matches the backend's
 * eligibility rules for the per-matter ECA budget.
 */
export function collectExplicitSelectionDocumentGuids(
  selectionIds: string[],
  folders: Folder[],
  documents: Document[],
): Set<string> {
  const folderGuids: string[] = [];
  const fileRefs: string[] = [];
  for (const id of selectionIds) {
    if (id.startsWith('folder:')) folderGuids.push(id.slice('folder:'.length));
    else if (id.startsWith('file:')) fileRefs.push(id.slice('file:'.length));
  }

  const subtreeAll = collectDocumentGuidsInFolderSubtrees(folderGuids, folders, documents);
  const guids = new Set<string>();
  for (const d of documents) {
    if (!subtreeAll.has(d.guid)) continue;
    if (!isCompletedNonDraftDocument(d)) continue;
    guids.add(d.guid);
  }
  for (const fileRef of fileRefs) {
    const doc = documents.find((x) => x.id === fileRef);
    if (doc && isCompletedNonDraftDocument(doc)) guids.add(doc.guid);
  }
  return guids;
}

/**
 * Drop redundant picks before persisting:
 *  - explicit picks under Client Documents (auto-included server-side),
 *  - `file:X` when X is also covered by a sibling `folder:Y` in the same selection
 *    (avoids persisting noise the worker would have to dedupe again).
 */
export function sanitizeEcaSavedSelection(
  ids: string[],
  subtreeFolderGuids: Set<string>,
  documents: Document[],
  clientSubtreeFolderIds: Set<number>,
  folders: Folder[] = [],
): string[] {
  const cdFileIds = collectFileReferenceIdsInFolders(
    documents,
    clientSubtreeFolderIds,
    subtreeFolderGuids,
  );

  // Surviving folder picks after dropping the Client Documents subtree.
  const survivingFolderGuids: string[] = [];
  for (const id of ids) {
    if (!id.startsWith('folder:')) continue;
    const g = id.slice('folder:'.length);
    if (!subtreeFolderGuids.has(g)) survivingFolderGuids.push(g);
  }
  const folderCoveredFileIds = new Set<string>();
  if (survivingFolderGuids.length > 0 && folders.length > 0) {
    const coveredGuids = collectDocumentGuidsInFolderSubtrees(
      survivingFolderGuids,
      folders,
      documents,
    );
    for (const d of documents) {
      if (coveredGuids.has(d.guid)) folderCoveredFileIds.add(`file:${d.id}`);
    }
  }

  return ids.filter((id) => {
    if (id.startsWith('folder:')) {
      const g = id.slice('folder:'.length);
      return !subtreeFolderGuids.has(g);
    }
    if (id.startsWith('file:')) {
      if (cdFileIds.has(id)) return false;
      if (folderCoveredFileIds.has(id)) return false;
      return true;
    }
    return true;
  });
}
